import type {AppearanceSettings} from '@packages/shared/types/appearanceSettings';
import type {
  DashboardUserTheme,
  DarkThemeStyle,
  GlobalThemePreference,
  GlobalThemeSelection,
  LegacyDashboardUserTheme,
  MatchMedia,
  OSThemePreference,
  ResolvedGlobalTheme,
  ThemeSurface,
  ThemesAnnouncementSurface,
  ThemeSurfaceOverrides,
} from '@packages/shared/types/globalInterfaceTheme';
import {THEME_SURFACES} from '@packages/shared/types/globalInterfaceTheme';

export type {
  DashboardUserTheme,
  DarkThemeStyle,
  GlobalThemePreference,
  GlobalThemeSelection,
  LegacyDashboardUserTheme,
  MatchMedia,
  OSThemePreference,
  ResolvedGlobalTheme,
  ThemeSurface,
  ThemeSelectionSource,
  ThemesAnnouncementSurface,
  ThemeSurfaceOverrides,
} from '@packages/shared/types/globalInterfaceTheme';

export const DEFAULT_DARK_THEME_STYLE = 'darkDefault' satisfies DarkThemeStyle;
export const PREFERS_COLOR_SCHEME_DARK_QUERY = '(prefers-color-scheme: dark)';
export const PREFERS_COLOR_SCHEME_LIGHT_QUERY = '(prefers-color-scheme: light)';
export const OS_THEME_PREFERENCE_COOKIE_NAME = '__Host-wfOsTheme';
export const OS_THEME_PREFERENCE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export function getOSThemePreferenceFromCookieValue(
  value: unknown
): Extract<OSThemePreference, 'light' | 'dark'> | undefined {
  return value === 'light' || value === 'dark' ? value : undefined;
}

export function isGlobalThemeSelection(
  value: unknown
): value is GlobalThemeSelection {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function getEffectiveGlobalThemeSelection(
  value: unknown
): GlobalThemeSelection | undefined {
  if (value == null) {
    return 'system';
  }

  return isGlobalThemeSelection(value) ? value : undefined;
}

export function isDashboardUserTheme(
  value: unknown
): value is DashboardUserTheme {
  return value === 'light' || value === DEFAULT_DARK_THEME_STYLE;
}

export function isLegacyDashboardUserTheme(
  value: unknown
): value is LegacyDashboardUserTheme {
  return isDashboardUserTheme(value) || value === 'dark';
}

export function isDarkThemeStyle(value: unknown): value is DarkThemeStyle {
  return (
    value === 'darkBrighter' ||
    value === DEFAULT_DARK_THEME_STYLE ||
    value === 'darkDarker'
  );
}

export function isAppearanceSetting(
  value: unknown
): value is AppearanceSettings {
  return value === 'light' || isDarkThemeStyle(value);
}

/**
 * Users who set Designer's legacy per-surface theme to "light" before the
 * global theme setting existed (Legacy Editor Users) have nothing
 * meaningful to be "announced" — silently carry that choice forward as
 * their global preference instead of showing the announcement.
 */
export function shouldSilentlyMigrateLegacyLightTheme(
  globalThemeSelection: GlobalThemeSelection | undefined,
  legacyAppearanceSettingsSelectedOption: AppearanceSettings | undefined
): boolean {
  return (
    !isGlobalThemeSelection(globalThemeSelection) &&
    legacyAppearanceSettingsSelectedOption === 'light'
  );
}

export function getDarkThemeStyleFromAppearanceSetting(
  appearanceSetting: unknown,
  fallbackDarkThemeStyle: DarkThemeStyle = DEFAULT_DARK_THEME_STYLE
): DarkThemeStyle {
  return isDarkThemeStyle(appearanceSetting)
    ? appearanceSetting
    : fallbackDarkThemeStyle;
}

export function getDashboardUserThemeFromResolvedTheme(
  resolvedTheme: ResolvedGlobalTheme
): DashboardUserTheme {
  return resolvedTheme === 'light' ? 'light' : 'darkDefault';
}

/**
 * `fallbackDarkThemeStyle` is only used when Dashboard resolves to light or
 * system; the Dashboard dark values map to `darkDefault` for legacy
 * compatibility. Unknown / missing `userTheme` falls back to system (not
 * light) so the UI matches the implicit-System default when nothing was
 * selected.
 */
export function normalizeDashboardUserThemeToGlobalPreference(
  userTheme: unknown,
  opts: {
    appearanceSettingsSelectedOption?: unknown;
    fallbackDarkThemeStyle?: DarkThemeStyle;
  } = {}
): GlobalThemePreference {
  if (userTheme === 'dark' || userTheme === DEFAULT_DARK_THEME_STYLE) {
    return {
      globalThemeSelection: 'dark',
      darkThemeStyle: DEFAULT_DARK_THEME_STYLE,
    };
  }

  if (userTheme === 'light') {
    return {
      globalThemeSelection: 'light',
      darkThemeStyle: getDarkThemeStyleFromAppearanceSetting(
        opts.appearanceSettingsSelectedOption,
        opts.fallbackDarkThemeStyle
      ),
    };
  }

  return {
    globalThemeSelection: 'system',
    darkThemeStyle: getDarkThemeStyleFromAppearanceSetting(
      opts.appearanceSettingsSelectedOption,
      opts.fallbackDarkThemeStyle
    ),
  };
}

export function normalizeDesignerAppearanceSettingToGlobalPreference(
  appearanceSettingsSelectedOption: unknown,
  opts: {
    defaultAppearanceSetting?: AppearanceSettings;
    fallbackDarkThemeStyle?: DarkThemeStyle;
  } = {}
): GlobalThemePreference {
  const appearanceSetting = isAppearanceSetting(
    appearanceSettingsSelectedOption
  )
    ? appearanceSettingsSelectedOption
    : (opts.defaultAppearanceSetting ?? DEFAULT_DARK_THEME_STYLE);
  const darkThemeStyle = getDarkThemeStyleFromAppearanceSetting(
    appearanceSetting,
    opts.fallbackDarkThemeStyle
  );

  if (appearanceSetting === 'light') {
    return {
      globalThemeSelection: 'light',
      darkThemeStyle,
    };
  }

  return {
    globalThemeSelection: 'dark',
    darkThemeStyle,
  };
}

export function resolveGlobalThemePreference(
  preference: GlobalThemePreference,
  osThemePreference: OSThemePreference
): ResolvedGlobalTheme {
  if (preference.globalThemeSelection === 'dark') {
    return preference.darkThemeStyle;
  }

  if (preference.globalThemeSelection === 'system') {
    return osThemePreference === 'dark' ? preference.darkThemeStyle : 'light';
  }

  return 'light';
}

export function getThemeSurfaceOverride(
  overrides: ThemeSurfaceOverrides | undefined,
  surface: ThemeSurface
): GlobalThemeSelection | undefined {
  const value = overrides?.[surface];
  return isGlobalThemeSelection(value) ? value : undefined;
}

export function getEffectiveThemeSelectionForSurface(
  globalThemeSelection: GlobalThemeSelection,
  overrides: ThemeSurfaceOverrides | undefined,
  surface: ThemeSurface
): GlobalThemeSelection {
  return getThemeSurfaceOverride(overrides, surface) ?? globalThemeSelection;
}

export function normalizeThemeSurfaceOverrides(
  value: unknown
): ThemeSurfaceOverrides | undefined {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const raw = value as Record<string, unknown>;
  // Preserve a canonical persisted map's identity. Flux persistence compares
  // plain objects by identity, so returning a new equivalent map during
  // hydration would schedule an unsolicited write on the first change.
  // Preserve `{}` as well. Callers use it to clear a previously stored map,
  // while malformed maps normalize to `undefined`.
  if (
    Object.keys(raw).every(
      (key) =>
        THEME_SURFACES.includes(key as (typeof THEME_SURFACES)[number]) &&
        isGlobalThemeSelection(raw[key])
    )
  ) {
    return value as ThemeSurfaceOverrides;
  }
  const next: ThemeSurfaceOverrides = {};
  for (const surface of THEME_SURFACES) {
    if (isGlobalThemeSelection(raw[surface])) {
      next[surface] = raw[surface];
    }
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Dashboard chrome surfaces: workspace chrome is always `dashboard`; when a
 * site slug is present, campaign sites use `campaign` and all other site
 * kinds still use `dashboard` (site settings stay on Dashboard chrome).
 */
export function getDashboardThemeSurface(
  siteKind: string | null | undefined,
  siteSlug: string | null | undefined
): ThemesAnnouncementSurface {
  if (!siteSlug) {
    return 'dashboard';
  }

  return siteKind === 'campaign' ? 'campaign' : 'dashboard';
}

export function getThemeSurfaceLabel(surface: ThemeSurface): string {
  switch (surface) {
    case 'dashboard':
      return 'Dashboard';
    case 'site':
      return 'Sites';
    case 'campaign':
      return 'Campaigns';
  }
}

// Shared by the Designer container and the AnalyticsStore reducer so a
// campaign site reports the same surface everywhere a theme selection is
// tracked, rather than each call site re-deriving the mapping.
//
// Accepts a string kind (not the SiteKind Zod enum) so this helper does not
// pull the site-access data-types barrel into lightweight DesignerApp import
// graphs/tests.
export const getThemeSurfaceForSiteKind = (
  kind: string | null | undefined
): Extract<ThemesAnnouncementSurface, 'site' | 'campaign'> =>
  kind === 'campaign' ? 'campaign' : 'site';

const getEffectiveThemeSelectionForResolution = ({
  globalThemeSelection,
  themeSelectionPreview,
  themeSurfaceOverrides,
  siteKind,
  surface,
}: {
  globalThemeSelection?: GlobalThemeSelection | null;
  themeSelectionPreview?: GlobalThemeSelection;
  themeSurfaceOverrides?: ThemeSurfaceOverrides;
  siteKind: string | null | undefined;
  surface?: ThemeSurface;
}): GlobalThemeSelection | undefined => {
  if (isGlobalThemeSelection(themeSelectionPreview)) {
    return themeSelectionPreview;
  }

  const effectiveGlobal =
    getEffectiveGlobalThemeSelection(globalThemeSelection);
  const effectiveSurface = getThemeSurfaceOverride(
    themeSurfaceOverrides,
    surface ?? getThemeSurfaceForSiteKind(siteKind)
  );

  if (!effectiveGlobal && !effectiveSurface) {
    // Persisted-but-invalid selection: do not follow the OS. A valid
    // surface override is still authoritative for that surface.
    return undefined;
  }

  return effectiveSurface ?? effectiveGlobal;
};

/**
 * Resolves the appearance theme for the requested surface: ephemeral
 * preview, then surface override, then account-global (with implicit System
 * when nothing is persisted).
 */
export function resolveAppearanceThemeForSurface({
  selectedOption,
  isGlobalInterfaceSettingsEnabled = false,
  globalThemeSelection,
  darkThemeStyle,
  osThemePreference,
  themeSelectionPreview,
  themeSurfaceOverrides,
  siteKind,
  surface,
}: {
  selectedOption: AppearanceSettings;
  isGlobalInterfaceSettingsEnabled?: boolean;
  globalThemeSelection?: GlobalThemeSelection | null;
  darkThemeStyle?: DarkThemeStyle;
  osThemePreference?: OSThemePreference;
  themeSelectionPreview?: GlobalThemeSelection;
  themeSurfaceOverrides?: ThemeSurfaceOverrides;
  siteKind: string | null | undefined;
  surface?: ThemeSurface;
}): AppearanceSettings {
  if (!isGlobalInterfaceSettingsEnabled) {
    return selectedOption;
  }

  const preferenceDarkThemeStyle = getDarkThemeStyleFromAppearanceSetting(
    darkThemeStyle,
    getDarkThemeStyleFromAppearanceSetting(
      selectedOption,
      DEFAULT_DARK_THEME_STYLE
    )
  );

  const effectiveSelection = getEffectiveThemeSelectionForResolution({
    globalThemeSelection,
    themeSelectionPreview,
    themeSurfaceOverrides,
    siteKind,
    surface,
  });
  if (!effectiveSelection) {
    return selectedOption;
  }

  return resolveGlobalThemePreference(
    {
      globalThemeSelection: effectiveSelection,
      darkThemeStyle: preferenceDarkThemeStyle,
    },
    osThemePreference ?? 'no-preference'
  );
}

/**
 * Resolves the effective theme selection for OS sync on a given surface:
 * preview, then an explicit surface override (or the surface derived from
 * `siteKind`), then the account selection, then implicit System.
 */
export function getEffectiveThemeSelectionForOsSync({
  themeSelectionPreview,
  globalThemeSelection,
  themeSurfaceOverrides,
  siteKind,
  surface,
}: {
  themeSelectionPreview?: GlobalThemeSelection;
  globalThemeSelection?: GlobalThemeSelection | null;
  themeSurfaceOverrides?: ThemeSurfaceOverrides;
  siteKind: string | null | undefined;
  surface?: ThemeSurface;
}): GlobalThemeSelection | undefined {
  return getEffectiveThemeSelectionForResolution({
    themeSelectionPreview,
    globalThemeSelection,
    themeSurfaceOverrides,
    siteKind,
    surface,
  });
}

export function updateGlobalThemeSelection(
  preference: GlobalThemePreference,
  globalThemeSelection: GlobalThemeSelection
): GlobalThemePreference {
  return {
    ...preference,
    globalThemeSelection,
  };
}

export function updateDarkThemeStyle(
  preference: GlobalThemePreference,
  darkThemeStyle: DarkThemeStyle
): GlobalThemePreference {
  return {
    ...preference,
    darkThemeStyle,
  };
}

export function getOSThemePreferenceFromMatchMedia(
  matchMedia?: MatchMedia
): OSThemePreference {
  if (!matchMedia) {
    return 'no-preference';
  }

  if (matchMedia(PREFERS_COLOR_SCHEME_DARK_QUERY).matches) {
    return 'dark';
  }

  if (matchMedia(PREFERS_COLOR_SCHEME_LIGHT_QUERY).matches) {
    return 'light';
  }

  return 'no-preference';
}

export function subscribeToOSThemePreferenceChange(
  matchMedia: MatchMedia | undefined,
  onChange: (osThemePreference: OSThemePreference) => void
): () => void {
  if (!matchMedia) {
    return () => {};
  }

  const mediaQueries = [
    matchMedia(PREFERS_COLOR_SCHEME_DARK_QUERY),
    matchMedia(PREFERS_COLOR_SCHEME_LIGHT_QUERY),
  ];
  let lastPreference = getOSThemePreferenceFromMatchMedia(matchMedia);
  const handleChange = () => {
    const nextPreference = getOSThemePreferenceFromMatchMedia(matchMedia);

    if (nextPreference !== lastPreference) {
      lastPreference = nextPreference;
      onChange(nextPreference);
    }
  };

  mediaQueries.forEach((mediaQuery) => {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
    }
  });

  return () => {
    mediaQueries.forEach((mediaQuery) => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleChange);
      }
    });
  };
}
